import { withStyles } from '@gdp/react-app/lib/helpers/withStyles';
import ApolloClient, { ApolloError } from 'apollo-client';
import * as moment from 'moment';
import * as React from 'react';
import { ApolloConsumer } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import { DateTimePicker } from 'react-widgets';

import { Button } from '../forms/Button';
import { GlairIcon } from '../GlairIcon';
import { WrappedColumn } from '../Table/WrappedColumn';
import { TableReportToolbar } from '../TableReportToolbar';

interface DateFilterToolbarProps {
  disableMaximumDuration?: boolean;
  error?: ApolloError;
  initialEndDate?: string;
  initialStartDate?: string;
  /** Extra control shown before the date pickers, e.g. which date column the range filters. */
  leadingFilter?: React.ReactNode;
  onDownloadAllClick?: (client: ApolloClient<any>, startDate?: string, endDate?: string) => void;
  onFilterClick: (startDate?: string, endDate?: string) => void;
  reportAttributes?: {
    columns: WrappedColumn[];
    filename: string;
    reportName: string;
    rows: any[];
  };
  showMaximumDuration?: boolean;
}

interface DateFilterToolbarState {
  endDate?: string;
  startDate?: string;
}

@withStyles(require('./DateFilterToolbar.scss'))
export class DateFilterToolbar extends React.Component<DateFilterToolbarProps, DateFilterToolbarState> {
  private readonly MAXIMUM_DURATION_IN_DAYS: number = 31;

  constructor(props: DateFilterToolbarProps) {
    super(props);

    this.state = {
      endDate: this.props.initialEndDate,
      startDate: this.props.initialStartDate
    };
  }

  componentDidUpdate(prevProps: DateFilterToolbarProps) {
    if (
      prevProps.initialStartDate !== this.props.initialStartDate ||
      prevProps.initialEndDate !== this.props.initialEndDate
    )
      this.setState({
        endDate: this.props.initialEndDate,
        startDate: this.props.initialStartDate
      });
  }

  render() {
    const { error, leadingFilter, onDownloadAllClick, onFilterClick, showMaximumDuration } = this.props;
    const { startDate, endDate } = this.state;
    return (
      <div className="DateFilterToolbar__container">
        <table className={`DateFilterToolbar__filter${leadingFilter ? ' DateFilterToolbar__filter--wide' : ''}`}>
          <tbody>
            <tr>
              {leadingFilter && <td className="DateFilterToolbar__leading">{leadingFilter}</td>}
              <td>
                <DateTimePicker
                  format="YYYY-MM-DD"
                  time={false}
                  // tslint:disable-next-line:jsx-no-lambda
                  onChange={date => this.handleStartDateFilterChange(date)}
                  max={endDate ? new Date(endDate) : undefined}
                  value={
                    // tslint:disable-next-line:jsx-no-multiline-js
                    (startDate && moment(startDate).toDate()) || undefined
                  }
                />
              </td>
              <td>
                <DateTimePicker
                  format="YYYY-MM-DD"
                  time={false}
                  // tslint:disable-next-line:jsx-no-lambda
                  onChange={date => this.handleEndDateFilterChange(date)}
                  min={startDate ? new Date(startDate) : undefined}
                  value={
                    // tslint:disable-next-line:jsx-no-multiline-js
                    (endDate && moment(endDate).toDate()) || undefined
                  }
                />
              </td>
              <td>
                <Button
                  figure="HeaderRegular"
                  color="DetailNeutral"
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => onFilterClick(startDate, endDate)}
                  disabled={this.isDateFilterInvalid() || this.isMaximumDurationExceeded()}
                >
                  <FormattedMessage id="form.action.filter" />
                </Button>
              </td>
              {// tslint:disable-next-line: jsx-no-multiline-js
              onDownloadAllClick && (
                <ApolloConsumer>
                  {// tslint:disable-next-line: jsx-no-multiline-js
                  (client: ApolloClient<any>) => (
                    <td>
                      <Button
                        figure="HeaderRegular"
                        color="DetailNeutral"
                        // tslint:disable-next-line:jsx-no-lambda
                        onClick={() => onDownloadAllClick(client, startDate, endDate)}
                        disabled={this.isDateFilterInvalid() || this.isMaximumDurationExceeded()}
                      >
                        <GlairIcon icon="CSV" /> &nbsp; &nbsp;
                        <FormattedMessage id="form.action.downloadCsv" />
                      </Button>
                    </td>
                  )}
                </ApolloConsumer>
              )}
            </tr>
          </tbody>
        </table>
        {this.props.reportAttributes && (
          <div className="DateFilterToolbar__tools">
            <TableReportToolbar
              reportName={this.props.reportAttributes.reportName}
              filename={this.props.reportAttributes.filename}
              columns={this.props.reportAttributes.columns}
              rows={this.props.reportAttributes.rows}
            />
          </div>
        )}
        <div className="DateFilterToolbar__error fg-error">{error && <p>{error.message}</p>}</div>
        <div className="DateFilterToolbar__error fg-error">
          {// tslint:disable-next-line: jsx-no-multiline-js
          this.isDateFilterInvalid() && (
            <p>
              <FormattedMessage id="validation.invalidStartDateOrEndDate" />
            </p>
          )}
        </div>
        <div className="DateFilterToolbar__error fg-error">
          {// tslint:disable-next-line: jsx-no-multiline-js
          (showMaximumDuration || this.isMaximumDurationExceeded()) && (
            <p>
              <FormattedMessage id="validation.maximumDurationExceeded" />
            </p>
          )}
        </div>
      </div>
    );
  }

  private readonly isDateFilterInvalid = (): boolean => {
    const { startDate, endDate } = this.state;
    return startDate && endDate ? moment(startDate).isAfter(moment(endDate)) : false;
  };

  private readonly isMaximumDurationExceeded = (): boolean => {
    const { startDate, endDate } = this.state;
    if (this.props.disableMaximumDuration) {
      return false;
    }
    if (startDate && endDate) {
      return this.getDurationInDays(startDate, endDate) > this.MAXIMUM_DURATION_IN_DAYS;
    }
    return false;
  };

  private readonly getDurationInDays = (startDate: string, endDate: string): number => {
    return Math.floor(moment.duration(moment(endDate).diff(moment(startDate))).asDays());
  };

  private readonly handleStartDateFilterChange = (date?: Date) => {
    this.setState({
      startDate: date
        ? moment(date)
            .startOf('day')
            .format()
        : undefined
    });
  };

  private readonly handleEndDateFilterChange = (date?: Date) => {
    this.setState({
      endDate: date
        ? moment(date)
            .endOf('day')
            .format()
        : undefined
    });
  };
}
